<template>
  <div :class="['ds-manager__component ds-editor', { small: isSmall }]">
    <div class="ds-manager__component-title">
      <r-title type="title-2">
        {{ $t("ds-manager:creation:empty-ds:title") }}
      </r-title>
    </div>
    <div class="ds-manager__component-content">
      <el-row class="ds-manager__component-row">
        <el-col class="r-col">
          <div class="ds-manager__component-subtitle">
            <r-text type="caption">
              {{ $t("ds-manager:creation:empty-ds:ds-name") }}</r-text
            >
          </div>
          <div class="ds-editor__rename-block">
            <el-input
              class="r-input"
              size="mini"
              :placeholder="`${$t('ds-manager:creation:empty-ds:ds-name')}`"
              v-model="dsName"
            />
          </div>
        </el-col>
      </el-row>
      <el-row class="ds-manager__component-row">
        <el-col class="r-col">
          <div class="ds-manager__component-subtitle">
            <r-text type="caption"> ID </r-text>
          </div>
          <div class="ds-manager__component-subtitle">
            <r-text type="caption"> {{ activeId }}</r-text>
          </div>
        </el-col>
      </el-row>
      <el-row class="ds-manager__component-row" v-if="attributes.length">
        <ds-manager-table
          :attributes="attributes"
          :initialAttributes="initialAttributes"
          :changedRows="changedRows"
          :fields="fields"
          :types="types"
          :datasourcesList="datasourcesList"
          @nameValidation="nameValidation"
          @addAttribute="addAttribute"
          @deleteAttribute="deleteAttribute"
          @deleteAllAttribute="deleteAllAttribute"
          @addChangedRow="addChangedRow"
          @deleteChangedRow="deleteChangedRow"
        />
      </el-row>
      <el-row
        class="ds-manager__component-row"
        type="flex"
        justify="space-between"
      >
        <div class="ds-manager__component-row-item">
          <el-button
            class="r-button"
            size="mini"
            type="danger"
            @click="beforeDeleteDatasourceAndUpdate"
          >
            {{ $t("ds-manager:creation:delete-ds") }}
          </el-button>
        </div>
        <div class="ds-manager__component-row-item">
          <el-button
            class="r-button simple"
            size="mini"
            type="primary"
            :disabled="!isEdited"
            @click="beforeCancelChanges"
          >
            {{ $t("ds-manager:creation:cancel") }}
          </el-button>
          <el-button
            class="r-button"
            size="mini"
            type="success"
            :disabled="!isEditReady || !isEdited"
            @click="beforeSaveEditing"
          >
            {{ $t("ds-manager:creation:edit-ds") }}
          </el-button>
        </div>
      </el-row>
    </div>
  </div>
</template>

<script>
import dsManagerTable from "./ds-manager-table/ds-manager-table";

import { notifyFactory, errorParser, parseObjectFields } from "@/utils";
import cloneDeep from "lodash.clonedeep";

const fields = [
  { label: "name", prop: "title", type: "name" },
  { label: "type", prop: "type", type: "belongs_to" },
  { label: "default", prop: "default", type: "string" },
  { label: "time field", prop: "time_field", type: "bool" }
];

export default {
  props: { menuClickHandler: { type: Function } },
  components: { dsManagerTable },
  data() {
    return {
      isSmall: true,

      initialName: "",
      dsName: "",
      isChangingTitle: false,

      types: [],
      geomTypes: [],
      geomType: "",

      attributes: [],
      initialAttributes: [],
      changedRows: [],
      newRows: [],
      deletedRows: [],
      allNamesCorrect: true,

      fields
    };
  },
  watch: {
    isEdited(val) {
      this.$store.commit("MODAL_WINDOW_ACTIVE_TOGGLER", val);
    }
  },
  computed: {
    activeId() {
      return this.$store.state.dsManager.activeDs.id;
    },
    isEdited() {
      return (
        !!this.changedRows.length ||
        !!this.newRows.length ||
        !!this.deletedRows.length ||
        !!(this.dsName !== this.initialName)
      );
    },
    isEditReady() {
      if (!this.attributes.length || !this.allNamesCorrect) return false;

      const isTitleEmpty = this.attributes.find(
        e => !e.title || !e.title.trim().length
      );
      const isTypeEmpty = this.attributes.find(
        e => !e.type || !e.type.trim().length
      );

      return !(isTitleEmpty || isTypeEmpty);
    },
    datasourcesList() {
      return (
        Object.keys(this.$store.state.datasources).map(
          ds => this.$store.state.datasources[ds]
        ) || []
      );
    }
  },
  methods: {
    async saveDsTitle() {
      const url = `objectInfo/auth.permission_objects`;
      const data = [{ id: this.activeId, name: this.dsName }];
      this.isChangingTitle = true;

      try {
        await this.$store.dispatch("PUT_REQUEST", { url, data });
        this.initialName = this.dsName;
        this.isChangingTitle = false;
        this.$store.commit("SET_UPDATE_DS_TREE", true);
      } catch (e) {
        errorParser.call(this, e);
        this.isChangingTitle = false;
      }
    },
    clearAll() {
      this.deleteAllAttribute();
      this.dsName = "";
      this.geomType = "";
    },
    async loadTypes() {
      try {
        const { data } = await this.$store.dispatch("REQUEST", {
          url: `datatypes`
        });

        this.types = data.base || [];
        this.geomTypes = data.geom || [];
      } catch (e) {
        console.log(e);
      }
    },
    nameValidation(val) {
      this.allNamesCorrect = val;
    },
    addChangedRow(title) {
      this.changedRows.push(title);
    },
    deleteChangedRow(index) {
      this.changedRows.splice(index, 1);
    },
    addAttribute() {
      const attribute = {
        title: null,
        type: "string",
        default: null,
        source_id: null,
        time_field: null
      };
      this.attributes.unshift(attribute);
      this.newRows.push(attribute);
    },
    deleteAttribute(row) {
      const index = this.attributes.findIndex(item => item.title === row.title);
      const newIndex = this.newRows.findIndex(item => item.title === row.title);
      this.attributes.splice(index, 1);
      if (newIndex > -1) this.newRows.splice(newIndex, 1);
      else this.deletedRows.push(row);
    },
    deleteAllAttribute() {
      this.attributes.splice(0, this.attributes.length);
    },
    beforeDeleteDatasourceAndUpdate() {
      const warningText = `${this.$t("modal.warning_text::delete")}`;
      const warningTitle = `${this.$t("modal.warning_title")}`;
      const confirmButtonText = `${this.$t("modal.confirm_button")}`;
      const cancelButtonText = `${this.$t("modal.cancel_button")}`;
      this.$confirm(warningText, warningTitle, {
        customClass: "r-message-box",
        type: "warning",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        confirmButtonText,
        cancelButtonText
      })
        .then(async () => {
          this.deleteDatasourceAndUpdate("id");
        })
        .catch(() => {});
    },
    async deleteDatasourceAndUpdate(id) {
      try {
        await this.deleteDatasource(this.activeId);
        const title = `${this.$t("notify:delete-success-title")}`;
        const message = `${this.$t("notify:delete-success-text")}`;
        this.$notify(notifyFactory("success", title, message));
        this.menuClickHandler("ds-manager-general");
      } catch (error) {
        const title = `${this.$t("notify:delete-fail-title")}`;
        const message = `${this.$t("notify:delete-fail-text")}`;
        errorParser.call(this, error, message, title);
      }
      return id;
    },
    deleteDatasource(id) {
      this.$store.commit("SET_UPDATE_DS_TREE", true);
      return this.$store.dispatch("DELETE_REQUEST", {
        url: `objectInfo/auth.permission_objects?id=${id}`
      });
    },
    beforeSaveEditing() {
      const warningText = `${this.$t("modal.warning_text::save")}`;
      const warningTitle = `${this.$t("modal.warning_title")}`;
      const confirmButtonText = `${this.$t("modal.confirm_button")}`;
      const cancelButtonText = `${this.$t("modal.cancel_button")}`;
      this.$confirm(warningText, warningTitle, {
        customClass: "r-message-box",
        type: "warning",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        confirmButtonText,
        cancelButtonText
      })
        .then(async () => {
          this.saveEditing();
        })
        .catch(() => {});
    },
    saveEditing() {
      if (this.dsName !== this.initialName) {
        this.saveDsTitle();
      }
      if (
        (this.changedRows && this.changedRows.length) ||
        (this.newRows && this.newRows.length)
      ) {
        this.saveDsChanges();
      }
    },
    async saveDsChanges() {
      const data = [];

      this.changedRows.forEach(title => {
        const object = this.attributes.find(item => item.source_name === title);
        if (object) {
          const newObject = {};
          if (object.title !== object.source_name) {
            newObject["new_title"] = object.title;
          }
          newObject.title = object.source_name;
          newObject["default"] = object.default;
          newObject["type"] = object.type;
          data.push({ ...newObject, _action: "updated" });
        }
      });
      this.deletedRows.forEach(item =>
        data.push({ _action: "deleted", title: item.title })
      );
      this.newRows.forEach(e => {
        const object = this.attributes.find(item => item.title === e.title);
        data.push({ ...object, _action: "created" });
      });
      data.forEach(item => !item.time_field && delete item.time_field);

      try {
        await this.$store.dispatch("PUT_REQUEST", {
          url: `objectFields/${this.activeId}`,
          data
        });
        this.newRows = [];
        this.changedRows = [];
        this.deletedRows = [];
        this.attributes.forEach(e => {
          if (e.title) e.source_name = e.title;
        });
        this.initialAttributes = cloneDeep(this.attributes);
        const title = `${this.$t("notify:save-success-title")}`;
        const message = `${this.$t("notify:save-success-text")}`;
        this.$notify(notifyFactory("success", title, message));
        this.$store.commit("SET_UPDATE_DS_TREE", true);
      } catch (error) {
        const title = `${this.$t("notify:save-fail-title")}`;
        const message = `${this.$t("notify:save-fail-text")}`;
        errorParser.call(this, error, message, title);
      }
    },
    beforeCancelChanges() {
      const warningText = `${this.$t("modal.warning_text::cancel")}`;
      const warningTitle = `${this.$t("modal.warning_title")}`;
      const confirmButtonText = `${this.$t("modal.confirm_button")}`;
      const cancelButtonText = `${this.$t("modal.cancel_button")}`;
      this.$confirm(warningText, warningTitle, {
        customClass: "r-message-box",
        type: "warning",
        closeOnPressEscape: false,
        closeOnClickModal: false,
        confirmButtonText,
        cancelButtonText
      })
        .then(async () => {
          this.cancelChanges();
        })
        .catch(() => {});
    },
    cancelChanges() {
      this.changedRows.forEach(item => {
        const initial = this.initialAttributes.find(
          object => object.title === item
        );
        const current = this.attributes.find(
          object => object.source_name === item
        );
        for (const prop in current) {
          current[prop] = initial[prop];
        }
      });
      this.newRows.forEach(e => {
        const index = this.attributes.findIndex(item => item.title === e.title);
        this.attributes.splice(index, 1);
      });
      this.deletedRows.forEach(item => this.attributes.unshift(item));
      this.deletedRows = [];
      this.changedRows = [];
      this.newRows = [];
      this.dsName = this.initialName;
    },
    async openDataSource(object) {
      const { id } = object;
      const data = this.$store.state.datasources[id];
      const { datatype, name } = data;

      this.initialName = name;
      this.dsName = name;

      if (datatype !== "model") {
        this.isSmall = false;
        try {
          const response = await this.$store.dispatch("REQUEST", {
            url: `objectFields/${id}`
          });

          this.attributes = parseObjectFields(response, true, true)
            .sort(e => {
              if (
                !e.system_field &&
                !e.read_only &&
                e.source_name !== "geom" &&
                e.source_name !== "id"
              )
                return -1;
            })
            .filter(
              e =>
                e.title !== "updated_user" &&
                e.title !== "created_user" &&
                !e.reflection
            );
          this.initialAttributes = cloneDeep(this.attributes);
        } catch (e) {
          errorParser.call(this, e);
        }
      }
    }
  },
  created() {
    this.loadTypes();
    this.openDataSource(this.$store.state.dsManager.activeDs);
  },
  beforeDestroy() {
    this.$store.commit("MODAL_WINDOW_ACTIVE_TOGGLER", false);
  }
};
</script>

<style lang="scss">
.ds-editor {
  .ds-manager__component {
    &-content {
      width: 800px;
      height: 480px;
    }
  }
  &.small {
    .ds-manager__component {
      &-content {
        width: 500px;
        height: 220px;
        .ds-manager__component-row {
          flex-direction: column-reverse;
          &-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          }
        }
      }
    }
  }
  .r-text,
  .r-text.caption-2 {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .el-table__body-wrapper {
    max-height: 200px !important;
  }
  &__rename-block {
    display: flex;
    &__controls {
      display: flex;
      margin-left: 8px;
      height: 28px;
      width: 76px;
      i {
        color: var(--text_primary) !important;
      }
    }
  }
}
</style>

<i18n>
{
  "ru": {
    "ds-manager:creation:empty-ds:title": "Редактирование источника данных",
    "ds-manager:creation:empty-ds:ds-name": "Название",

    "ds-manager:creation:edit-ds": "Отредактировать источник данных",
    "ds-manager:creation:delete-ds": "Удалить источник данных",
    "ds-manager:creation:cancel": "Отмена",

    "ds-manager:creation:create-success-title": "Запрос отправлен",
    "ds-manager:creation:create-success-text": "Запрос на создание источника данных отправлен. Номер задачи - ",
    
    "modal.confirm_button": "Да",
    "modal.cancel_button": "Нет",
    "modal.warning_title": "Предупреждение",
    "modal.warning_text::delete": "Источник данных будет удалён без возможности восстановления. Продолжить?",
    "modal.warning_text::cancel": "Все изменения будут отменены. Продолжить?",
    "modal.warning_text::save": "Источник данных будет изменен без возможности восстановления. Продолжить?",

    "notify:delete-success-title": "Удаление выполнено",
    "notify:delete-success-text": "Удаление выполнено успешно",
    "notify:delete-fail-title": "Произошла ошибка",
    "notify:delete-fail-text": "Ошибка при удалении, повторите попытку",

    "notify:save-success-title": "Сохранение выполнено",
    "notify:save-success-text": "Сохранение выполнено успешно",
    "notify:save-fail-title": "Произошла ошибка",
    "notify:save-fail-text": "Ошибка при сохранении, повторите попытку"
  },
  "en": {
    "ds-manager:creation:empty-ds:title": "Create an empty data source",
    "ds-manager:creation:empty-ds:ds-name": "Name",
    
    "ds-manager:creation:no-match": "No match",
    
    "ds-manager:creation:edit-ds": "Edit data source",
    "ds-manager:creation:delete-ds": "Delete data source",
    "ds-manager:creation:cancel": "Cancel",

    "ds-manager:creation:create-success-title": "Request sent",
    "ds-manager:creation:create-success-text": "A request to create a data source has been sent. Task number -",
    
    "modal.confirm_button": "Yes",
    "modal.cancel_button": "No",
    "modal.warning_title": "Warning",
    "modal.warning_text::delete": "Current data source will be permanently deleted. Continue?",
    "modal.warning_text::cancel": "All changes will be canceled. Continue?",
    "modal.warning_text::save": "Current data source will be permanently changed. Continue?",

    "notify:delete-success-title": "Delete successfully",
    "notify:delete-success-text": "Delete successfully",
    "notify:delete-fail-title": "Error",
    "notify:delete-fail-text": "Delete error, try again",

    "notify:save-success-title": "Save successfully",
    "notify:save-success-text": "Save successfully",
    "notify:save-fail-title": "Error",
    "notify:save-fail-text": "Save error, try again"
  }
}
</i18n>
